import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../../component/layout/Layout";
import {
  getMyAccountStatus,
  listMessageAds,
  cancelAd,
  deleteMessageAd,
  getReport,
  type MomentAccountStatus,
  type MomentAdDto,
} from "../api/momentApi";
import { canEditReadyMessage } from "../utils/dateTimeUtils";
import {
  STATUS_LABEL,
  STATUS_BADGE,
  MESSAGE_TYPE_LABEL,
  DATE_FILTER_TYPE_LABEL,
  DATE_PRESET_OPTIONS,
} from "../data/momentConstants";

export default function MomentListPage() {
  return (
    <Layout>
      <MomentListGuard />
    </Layout>
  );
}

function MomentListGuard() {
  const navigate = useNavigate();
  const [account, setAccount] = useState<MomentAccountStatus | null | undefined>(undefined);

  useEffect(() => {
    getMyAccountStatus()
      .then((data) => setAccount(data ?? null))
      .catch(() => setAccount(null));
  }, []);

  useEffect(() => {
    if (account === undefined) return;
    const isReady = account !== null && account.status === "ACTIVE" && !!account.kakaoAdAccountId;
    if (!isReady) {
      navigate("/moment/description", { replace: true });
    }
  }, [account, navigate]);

  if (account === undefined) {
    return (
      <div className="moment-page tw-min-h-[calc(100vh-200px)] tw-flex tw-items-center tw-justify-center tw-text-gray-400 tw-text-sm">
        불러오는 중...
      </div>
    );
  }

  const isReady = account !== null && account.status === "ACTIVE" && !!account.kakaoAdAccountId;
  if (!isReady) {
    return null;
  }

  return <MomentListContent />;
}

function MomentListContent() {
  const navigate = useNavigate();
  const [list, setList] = useState<MomentAdDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [adName, setAdName] = useState("");
  const [messageType, setMessageType] = useState("");
  const [dateFilterType, setDateFilterType] = useState<"sendDate" | "createdDate">("sendDate");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // 보고서 모달
  const [reportTarget, setReportTarget] = useState<MomentAdDto | null>(null);
  const [reportPreset, setReportPreset] = useState("LAST_7DAY");
  const [reportData, setReportData] = useState<object | null>(null);
  const [reportLoading, setReportLoading] = useState(false);

  const PAGE_SIZE = 10;

  const fetchList = async (
    p = page,
    overrides?: {
      status?: string;
      adName?: string;
      messageType?: string;
      dateFilterType?: "sendDate" | "createdDate";
      sortDirection?: "ASC" | "DESC";
      dateFrom?: string;
      dateTo?: string;
    }
  ) => {
    const nextStatus = overrides?.status ?? statusFilter;
    const nextAdName = overrides?.adName ?? adName;
    const nextMessageType = overrides?.messageType ?? messageType;
    const nextDateFilterType = overrides?.dateFilterType ?? dateFilterType;
    const nextSortDirection = overrides?.sortDirection ?? sortDirection;
    const nextDateFrom = overrides?.dateFrom ?? dateFrom;
    const nextDateTo = overrides?.dateTo ?? dateTo;

    setIsLoading(true);
    try {
      const res = await listMessageAds({
        page: p,
        size: PAGE_SIZE,
        status: nextStatus || undefined,
        adName: nextAdName || undefined,
        messageType: nextMessageType || undefined,
        sendDateFrom: nextDateFilterType === "sendDate" ? nextDateFrom || undefined : undefined,
        sendDateTo: nextDateFilterType === "sendDate" ? nextDateTo || undefined : undefined,
        createdDateFrom: nextDateFilterType === "createdDate" ? nextDateFrom || undefined : undefined,
        createdDateTo: nextDateFilterType === "createdDate" ? nextDateTo || undefined : undefined,
        sortDirection: nextSortDirection,
      });
      setList(res.list);
      setTotal(res.total);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "목록 조회 중 오류가 발생했습니다.";
      alert(msg);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleSearch = () => {
    setPage(0);
    fetchList(0);
  };

  const handleReset = () => {
    setStatusFilter("");
    setAdName("");
    setMessageType("");
    setDateFilterType("sendDate");
    setSortDirection("DESC");
    setDateFrom("");
    setDateTo("");
    setPage(0);
    fetchList(0, {
      status: "",
      adName: "",
      messageType: "",
      dateFilterType: "sendDate",
      sortDirection: "DESC",
      dateFrom: "",
      dateTo: "",
    });
  };

  const handleCancelAd = async (ad: MomentAdDto) => {
    if (!window.confirm(`'${ad.adName}' 발송을 취소하시겠습니까?`)) return;
    try {
      await cancelAd(ad.kakaoAdId);
      fetchList();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "발송 취소 중 오류가 발생했습니다.";
      alert(msg);
    }
  };

  const handleDelete = async (ad: MomentAdDto) => {
    if (!window.confirm(`'${ad.adName}' 광고를 삭제하시겠습니까?`)) return;
    try {
      await deleteMessageAd(ad.kakaoAdId);
      fetchList();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "삭제 중 오류가 발생했습니다.";
      alert(msg);
    }
  };

  const handleLoadDraft = (ad: MomentAdDto) => {
    navigate(`/moment/create?draftAdId=${encodeURIComponent(ad.kakaoAdId)}`);
  };

  const handleOpenReport = (ad: MomentAdDto) => {
    if (ad.status === "FINISHED") {
      navigate(`/moment/report/${encodeURIComponent(ad.kakaoAdId)}`, { state: { ad } });
      return;
    }
    setReportTarget(ad);
    setReportData(null);
    setReportPreset("LAST_7DAY");
  };

  const handleFetchReport = async () => {
    if (!reportTarget) return;
    setReportLoading(true);
    try {
      const data = await getReport({
        messageAdId: reportTarget.kakaoAdId,
        datePreset: reportPreset,
        metricsGroups: ["MESSAGE", "MESSAGE_CLICK"],
      });
      setReportData(data as object);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "보고서 조회 중 오류가 발생했습니다.";
      alert(msg);
    } finally {
      setReportLoading(false);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="moment-page tw-max-w-[1200px] tw-mx-auto tw-px-6 tw-py-6">
      <h1 className="tw-text-2xl tw-font-bold tw-text-gray-800 tw-mb-2">메시지 광고 관리</h1>
      <p className="tw-text-sm tw-text-gray-500 tw-mb-6">등록된 카카오모먼트 브랜드 메시지 광고를 관리합니다.</p>

      <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-p-5 tw-mb-4">
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 xl:tw-grid-cols-[minmax(220px,1.6fr)_minmax(120px,0.8fr)_minmax(120px,0.8fr)_minmax(120px,0.8fr)_minmax(110px,0.7fr)_minmax(160px,1fr)_minmax(160px,1fr)] tw-gap-4 tw-items-end">
          <label className="tw-flex tw-flex-col tw-gap-2">
            <span className="tw-text-sm tw-font-medium tw-text-gray-700">광고명</span>
            <input
              value={adName}
              onChange={(e) => setAdName(e.target.value)}
              className="tw-h-11 tw-rounded-lg tw-border tw-border-gray-300 tw-px-3 tw-text-sm"
              placeholder="광고명을 입력하세요"
            />
          </label>
          <label className="tw-flex tw-flex-col tw-gap-2">
            <span className="tw-text-sm tw-font-medium tw-text-gray-700">메시지 유형</span>
            <select
              value={messageType}
              onChange={(e) => setMessageType(e.target.value)}
              className="tw-h-11 tw-rounded-lg tw-border tw-border-gray-300 tw-px-3 tw-text-sm"
            >
              <option value="">전체</option>
              {Object.entries(MESSAGE_TYPE_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="tw-flex tw-flex-col tw-gap-2">
            <span className="tw-text-sm tw-font-medium tw-text-gray-700">상태</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="tw-h-11 tw-rounded-lg tw-border tw-border-gray-300 tw-px-3 tw-text-sm"
            >
              <option value="">전체</option>
              {["DRAFT", "READY", "SENDING", "FINISHED", "CANCELED", "ADMIN_STOP"].map((status) => (
                <option key={status} value={status}>
                  {STATUS_LABEL[status]}
                </option>
              ))}
            </select>
          </label>
          <label className="tw-flex tw-flex-col tw-gap-2">
            <span className="tw-text-sm tw-font-medium tw-text-gray-700">날짜 기준</span>
            <select
              value={dateFilterType}
              onChange={(e) => setDateFilterType(e.target.value as "sendDate" | "createdDate")}
              className="tw-h-11 tw-rounded-lg tw-border tw-border-gray-300 tw-px-3 tw-text-sm"
            >
              {Object.entries(DATE_FILTER_TYPE_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="tw-flex tw-flex-col tw-gap-2">
            <span className="tw-text-sm tw-font-medium tw-text-gray-700">정렬</span>
            <select
              value={sortDirection}
              onChange={(e) => setSortDirection(e.target.value as "ASC" | "DESC")}
              className="tw-h-11 tw-rounded-lg tw-border tw-border-gray-300 tw-px-3 tw-text-sm"
            >
              <option value="DESC">최신순</option>
              <option value="ASC">오래된순</option>
            </select>
          </label>
          <label className="tw-flex tw-flex-col tw-gap-2">
            <span className="tw-text-sm tw-font-medium tw-text-gray-700">시작일</span>
            <input
              type="date"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => {
                const nextDateFrom = e.target.value;
                setDateFrom(nextDateFrom);
                if (dateTo && nextDateFrom && nextDateFrom > dateTo) {
                  setDateTo(nextDateFrom);
                }
              }}
              className="tw-h-11 tw-rounded-lg tw-border tw-border-gray-300 tw-px-3 tw-text-sm"
            />
          </label>
          <label className="tw-flex tw-flex-col tw-gap-2">
            <span className="tw-text-sm tw-font-medium tw-text-gray-700">종료일</span>
            <input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => {
                const nextDateTo = e.target.value;
                setDateTo(nextDateTo);
                if (dateFrom && nextDateTo && nextDateTo < dateFrom) {
                  setDateFrom(nextDateTo);
                }
              }}
              className="tw-h-11 tw-rounded-lg tw-border tw-border-gray-300 tw-px-3 tw-text-sm"
            />
          </label>
        </div>

        <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-4">
          <button
            onClick={handleReset}
            className="tw-h-11 tw-px-4 tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white tw-text-sm tw-font-medium tw-text-gray-600"
          >
            초기화
          </button>
          <button
            onClick={handleSearch}
            className="tw-h-11 tw-px-4 tw-rounded-lg tw-bg-[#5D87FF] tw-text-white tw-text-sm tw-font-medium tw-border-0"
          >
            조회
          </button>
        </div>
      </div>

      {/* 목록 테이블 */}
      <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
        <table className="tw-w-full tw-table-fixed tw-text-sm">
          <colgroup>
            <col className="tw-w-[32%]" />
            <col className="tw-w-[10%]" />
            <col className="tw-w-[10%]" />
            <col className="tw-w-[12%]" />
            <col className="tw-w-[10%]" />
            <col className="tw-w-[26%]" />
          </colgroup>
          <thead className="tw-bg-gray-50">
            <tr>
              <th className="tw-text-left tw-px-4 tw-py-3 tw-text-gray-600 tw-font-semibold">광고명</th>
              <th className="tw-text-center tw-px-3 tw-py-3 tw-text-gray-600 tw-font-semibold tw-whitespace-nowrap">유형</th>
              <th className="tw-text-center tw-px-3 tw-py-3 tw-text-gray-600 tw-font-semibold tw-whitespace-nowrap">상태</th>
              <th className="tw-text-center tw-px-3 tw-py-3 tw-text-gray-600 tw-font-semibold tw-whitespace-nowrap">발송일</th>
              <th className="tw-text-right tw-px-3 tw-py-3 tw-text-gray-600 tw-font-semibold tw-whitespace-nowrap">비용</th>
              <th className="tw-text-center tw-px-4 tw-py-3 tw-text-gray-600 tw-font-semibold tw-whitespace-nowrap">액션</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="tw-text-center tw-py-10 tw-text-gray-400">
                  불러오는 중...
                </td>
              </tr>
            ) : list.length === 0 ? (
              <tr>
                <td colSpan={6} className="tw-text-center tw-py-10 tw-text-gray-400">
                  등록된 메시지 광고가 없습니다.
                </td>
              </tr>
            ) : (
              list.map((ad) => (
                <tr key={ad.uid} className="tw-border-t tw-border-gray-100 hover:tw-bg-gray-50">
                  <td className="tw-px-4 tw-py-4">
                    <div
                      className="tw-font-medium tw-text-gray-800 tw-break-words"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        lineHeight: "1.4",
                        maxHeight: "2.8em",
                      }}
                    >
                      {ad.adName}
                    </div>
                  </td>
                  <td className="tw-text-center tw-px-3 tw-py-4 tw-text-gray-600 tw-whitespace-nowrap">
                    {MESSAGE_TYPE_LABEL[ad.messageType] ?? ad.messageType}
                  </td>
                  <td className="tw-text-center tw-px-3 tw-py-4 tw-whitespace-nowrap">
                    <span className={`tw-inline-flex tw-px-2 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium ${STATUS_BADGE[ad.status] ?? "tw-bg-gray-100 tw-text-gray-600"}`}>
                      {STATUS_LABEL[ad.status] ?? (ad.status === "KAKAO_ADMIN_STOP" ? "관리자정지" : ad.status)}
                    </span>
                  </td>
                  <td className="tw-text-center tw-px-3 tw-py-4 tw-text-gray-600">
                    {ad.sendDate ? (
                      <div className="tw-leading-tight">
                        <div className="tw-whitespace-nowrap">{ad.sendDate.replace("T", " ").substring(0, 10)}</div>
                        <div className="tw-whitespace-nowrap">
                          {(() => {
                            const normalizedSendDate = ad.sendDate.replace("T", " ");
                            const timePart = normalizedSendDate.length >= 19
                              ? normalizedSendDate.substring(11, 19)
                              : `${normalizedSendDate.substring(11, 16)}:00`;
                            return timePart;
                          })()}
                        </div>
                      </div>
                    ) : "-"}
                  </td>
                  <td className="tw-text-right tw-px-3 tw-py-4 tw-text-gray-700 tw-whitespace-nowrap">
                    {ad.totalAmount != null ? `${ad.totalAmount.toLocaleString()}원` : "-"}
                  </td>
                  <td className="tw-text-center tw-px-4 tw-py-4 tw-whitespace-nowrap">
                    <div className="tw-flex tw-flex-nowrap tw-justify-center tw-gap-1.5">
                      {/* 보고서/상세보기: SENDING, FINISHED */}
                      {["SENDING", "FINISHED"].includes(ad.status) && (
                        <button
                          onClick={() => handleOpenReport(ad)}
                          className="tw-text-xs tw-px-2.5 tw-py-1.5 tw-bg-blue-50 hover:tw-bg-blue-100 tw-text-blue-700 tw-rounded-lg tw-font-medium tw-border-0 tw-whitespace-nowrap"
                        >
                          {ad.status === "FINISHED" ? "상세보기" : "보고서"}
                        </button>
                      )}
                      {/* 수정하기 / 예약하기: DRAFT */}
                      {ad.status === "DRAFT" && (
                        <>
                          <button
                            onClick={() => handleLoadDraft(ad)}
                            className="tw-text-xs tw-px-2.5 tw-py-1.5 tw-bg-blue-50 hover:tw-bg-blue-100 tw-text-blue-700 tw-rounded-lg tw-font-medium tw-border-0 tw-whitespace-nowrap"
                          >
                            수정하기
                          </button>
                          <button
                            onClick={() => navigate(`/moment/reserve/${encodeURIComponent(ad.kakaoAdId)}`)}
                            className="tw-text-xs tw-px-2.5 tw-py-1.5 tw-bg-emerald-50 hover:tw-bg-emerald-100 tw-text-emerald-700 tw-rounded-lg tw-font-medium tw-border-0 tw-whitespace-nowrap"
                          >
                            예약하기
                          </button>
                        </>
                      )}
                      {/* 예약 수정 / 발송 취소: READY */}
                      {ad.status === "READY" && (
                        <>
                          {canEditReadyMessage(ad.sendDate) && (
                            <>
                              <button
                                onClick={() => navigate(`/moment/create?draftAdId=${encodeURIComponent(ad.kakaoAdId)}`)}
                                className="tw-text-xs tw-px-2.5 tw-py-1.5 tw-bg-indigo-50 hover:tw-bg-indigo-100 tw-text-indigo-700 tw-rounded-lg tw-font-medium tw-border-0 tw-whitespace-nowrap"
                              >
                                메시지 수정
                              </button>
                              <button
                                onClick={() => navigate(`/moment/reserve/${encodeURIComponent(ad.kakaoAdId)}`)}
                                className="tw-text-xs tw-px-2.5 tw-py-1.5 tw-bg-blue-50 hover:tw-bg-blue-100 tw-text-blue-700 tw-rounded-lg tw-font-medium tw-border-0 tw-whitespace-nowrap"
                              >
                                예약 수정
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleCancelAd(ad)}
                            className="tw-text-xs tw-px-2.5 tw-py-1.5 tw-bg-yellow-50 hover:tw-bg-yellow-100 tw-text-yellow-700 tw-rounded-lg tw-font-medium tw-border-0 tw-whitespace-nowrap"
                          >
                            발송 취소
                          </button>
                        </>
                      )}
                      {/* 내용 확인: CANCELED, ADMIN_STOP (read-only 창작 페이지로 이동) */}
                      {["CANCELED", "ADMIN_STOP", "KAKAO_ADMIN_STOP"].includes(ad.status) && (
                        <button
                          onClick={() => navigate(`/moment/create?draftAdId=${encodeURIComponent(ad.kakaoAdId)}&readonly=true`)}
                          className="tw-text-xs tw-px-2.5 tw-py-1.5 tw-bg-gray-50 hover:tw-bg-gray-100 tw-text-gray-600 tw-rounded-lg tw-font-medium tw-border-0 tw-whitespace-nowrap"
                        >
                          내용 확인
                        </button>
                      )}
                      {/* 삭제: DRAFT, CANCELED, ADMIN_STOP */}
                      {["DRAFT", "CANCELED", "ADMIN_STOP", "KAKAO_ADMIN_STOP"].includes(ad.status) && (
                        <button
                          onClick={() => handleDelete(ad)}
                          className="tw-text-xs tw-px-2.5 tw-py-1.5 tw-bg-red-50 hover:tw-bg-red-100 tw-text-red-600 tw-rounded-lg tw-font-medium tw-border-0 tw-whitespace-nowrap"
                        >
                          삭제
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      {totalPages >= 1 && (
        <div className="tw-flex tw-justify-center tw-items-center tw-gap-2 tw-mt-4">
          <button
            onClick={() => { setPage(0); fetchList(0); }}
            disabled={page === 0}
            className="tw-h-9 tw-px-3 tw-rounded-lg tw-bg-white tw-border tw-border-gray-300 tw-text-sm tw-text-gray-600 disabled:tw-opacity-40"
          >
            처음
          </button>
          <button
            onClick={() => { const nextPage = Math.max(page - 1, 0); setPage(nextPage); fetchList(nextPage); }}
            disabled={page === 0}
            className="tw-h-9 tw-px-3 tw-rounded-lg tw-bg-white tw-border tw-border-gray-300 tw-text-sm tw-text-gray-600 disabled:tw-opacity-40"
          >
            이전
          </button>
          {Array.from({ length: Math.max(totalPages, 1) }, (_, i) => (
            <button
              key={i}
              onClick={() => { setPage(i); fetchList(i); }}
              className={`tw-border-0 tw-w-9 tw-h-9 tw-rounded-lg tw-text-sm tw-font-medium ${
                page === i
                  ? "tw-bg-[#5D87FF] tw-text-white"
                  : "tw-bg-white tw-border tw-border-gray-300 tw-text-gray-600 hover:tw-bg-gray-50"
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => { const nextPage = Math.min(page + 1, Math.max(totalPages - 1, 0)); setPage(nextPage); fetchList(nextPage); }}
            disabled={page >= totalPages - 1}
            className="tw-h-9 tw-px-3 tw-rounded-lg tw-bg-white tw-border tw-border-gray-300 tw-text-sm tw-text-gray-600 disabled:tw-opacity-40"
          >
            다음
          </button>
          <button
            onClick={() => { const nextPage = Math.max(totalPages - 1, 0); setPage(nextPage); fetchList(nextPage); }}
            disabled={page >= totalPages - 1}
            className="tw-h-9 tw-px-3 tw-rounded-lg tw-bg-white tw-border tw-border-gray-300 tw-text-sm tw-text-gray-600 disabled:tw-opacity-40"
          >
            마지막
          </button>
        </div>
      )}

      {/* 보고서 모달 */}
      {reportTarget && (
        <div className="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-2xl tw-p-6 tw-w-full tw-max-w-lg tw-shadow-xl tw-max-h-[80vh] tw-overflow-y-auto">
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
              <h3 className="tw-font-bold tw-text-lg">보고서 조회</h3>
              <button
                onClick={() => { setReportTarget(null); setReportData(null); }}
                className="tw-text-gray-400 hover:tw-text-gray-600 tw-text-xl"
              >
                ×
              </button>
            </div>

            <p className="tw-text-sm tw-text-gray-600 tw-mb-4">{reportTarget.adName}</p>

            <div className="tw-flex tw-gap-2 tw-mb-4">
              {DATE_PRESET_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setReportPreset(opt.value)}
                  className={`tw-flex-1 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-border ${
                    reportPreset === opt.value
                      ? "tw-bg-[#5D87FF] tw-text-white tw-border-[#5D87FF]"
                      : "tw-bg-white tw-text-gray-600 tw-border-gray-300"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <button
              onClick={handleFetchReport}
              disabled={reportLoading}
              className="tw-w-full tw-py-2.5 tw-bg-[#5D87FF] tw-text-white tw-rounded-lg tw-font-bold tw-mb-4 disabled:tw-opacity-50"
            >
              {reportLoading ? "조회 중..." : "보고서 조회"}
            </button>

            {reportData && (
              <div className="tw-bg-gray-50 tw-rounded-xl tw-p-4">
                <ReportResult data={reportData} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ReportResult({ data }: { data: object }) {
  if (!data) return <p className="tw-text-sm tw-text-gray-500">데이터 없음</p>;

  // Kakao 보고서 응답 렌더링 (rows 배열 기반)
  const rows = (data as Record<string, unknown>).rows;
  if (Array.isArray(rows) && rows.length > 0) {
    const firstRow = rows[0] as Record<string, unknown>;
    const metrics = firstRow.metrics as Record<string, unknown> | undefined;
    if (metrics) {
      return (
        <div className="tw-space-y-2 tw-text-sm">
          {Object.entries(metrics).map(([key, val]) => (
            <div key={key} className="tw-flex tw-justify-between">
              <span className="tw-text-gray-500">{key}</span>
              <span className="tw-font-semibold">{String(val)}</span>
            </div>
          ))}
        </div>
      );
    }
  }

  // 원본 데이터 fallback
  return (
    <pre className="tw-text-xs tw-text-gray-600 tw-overflow-auto">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}
